import { Portrait } from "../portrait/Portrait";
import { capitalise, lastDealingsLine, seemsPhrase, towardYouPhrase } from "../people";
import type { PersonCard, ProfileView } from "../types";
import { PersonRef } from "./PersonRef";

const GROUPS: { id: string; label: string; test: (grade: string) => boolean }[] = [
  { id: "partners", label: "Partners", test: (g) => g.includes("partner") },
  { id: "fee", label: "Fee-earners", test: (g) => ["associate", "solicitor", "trainee", "paralegal"].some((x) => g.includes(x)) },
  { id: "support", label: "Management and support", test: () => true },
];

/** A belief register, not a character sheet (19 §4.4): who works here, and how they seem to you. Words, never numbers. */
export function PeoplePanel({ people, profiles = {}, youId = "player" }: { people: PersonCard[]; profiles?: Record<string, ProfileView> | undefined; youId?: string }) {
  const others = people.filter((p) => p.id !== youId);
  const placed = new Set<string>();
  return (
    <section className="panel" aria-labelledby="people-title">
      <h2 id="people-title" className="panel-title">People</h2>
      {GROUPS.map((g) => {
        const members = others.filter((p) => !placed.has(p.id) && g.test(p.grade.toLowerCase()));
        members.forEach((p) => placed.add(p.id));
        if (members.length === 0) return null;
        return (
          <div key={g.id} className="people-group">
            <h3 className="people-group__title">{g.label}</h3>
            <ul className="people-grid">
              {members.map((p) => {
                const profile = profiles[p.id];
                const seems = profile ? seemsPhrase(profile.demeanour.seems) : null;
                const last = profile ? lastDealingsLine(profile.lastDealings) : null;
                return (
                  <li key={p.id} className="person-card">
                    <Portrait portraitId={p.portraitId} seed={p.id} name={p.name} role={p.role} grade={p.grade} size={72} />
                    <div>
                      <p className="person-card__name"><PersonRef personId={p.id} person={p}>{p.name}</PersonRef></p>
                      <p className="person-card__role">{p.role}</p>
                      {p.availability !== "in" && <p className="person-card__away">{p.availability === "away" ? "Away from work" : "Has resigned"}</p>}
                      {profile && (
                        <p className="person-card__seem">
                          {capitalise(towardYouPhrase(profile.demeanour.towardYou))}{seems ? `; ${seems.toLowerCase()}` : ""}
                        </p>
                      )}
                      {last && <p className="person-card__last">{last}</p>}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
      <p className="panel-note">As you see it — people don't always show how they feel. Your history with each person builds up as you deal with them.</p>
    </section>
  );
}
